/* Community poll shared pieces: usePolls hook + PollCard.
 * The old in-feed strip retired — polls now live in IdeasVotePanel (desktop rail)
 * and WallSpotlightMobile (mobile modal). */
import { useState, useEffect, useCallback } from 'react';
import React from 'react';
import { Lightbulb } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const authHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${localStorage.getItem('beatseek_token') || ''}`
});

const daysLeft = (expiresAt) => {
  const ms = new Date(expiresAt) - new Date();
  if (ms <= 0) return 'ending soon';
  const d = Math.floor(ms / 86400000);
  if (d >= 1) return `${d} day${d === 1 ? '' : 's'} left`;
  const h = Math.max(1, Math.floor(ms / 3600000));
  return `${h}h left`;
};

export const usePolls = (userId) => {
  const [polls, setPolls] = useState([]);
  const [optedOut, setOptedOut] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/polls/active${userId ? `?user_id=${userId}` : ''}`);
      if (r.ok) {
        const d = await r.json();
        setOptedOut(!!d.opted_out);
        setPolls(d.polls || []);
      }
    } catch (e) { /* noop */ }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const vote = async (pollId, choice) => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/polls/${pollId}/vote`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify({ vote: choice })
      });
      if (r.ok) {
        const d = await r.json();
        setPolls(prev => prev.map(p => p.id === pollId ? { ...p, ...d } : p));
      }
    } catch (e) { /* noop */ }
  };

  const dismiss = async (pollId) => {
    setPolls(prev => prev.filter(p => p.id !== pollId));
    try {
      await fetch(`${BACKEND_URL}/api/polls/${pollId}/dismiss`, { method: 'POST', headers: authHeaders() });
    } catch (e) { /* noop */ }
  };

  const setOptOutPref = async (optOut) => {
    setOptedOut(optOut);
    if (optOut) setPolls([]);
    try {
      await fetch(`${BACKEND_URL}/api/polls/opt-out`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ opt_out: optOut }) });
      if (!optOut) load();
    } catch (e) { /* noop */ }
  };

  const unvotedCount = optedOut ? 0 : polls.filter(p => !p.user_vote).length;

  return { polls, optedOut, vote, dismiss, setOptOutPref, reload: load, unvotedCount };
};

export const PollCard = ({ poll, onVote }) => {
  const voted = !!poll.user_vote;
  const total = (poll.build_votes || 0) + (poll.pass_votes || 0);
  const pct = (n) => total === 0 ? 0 : Math.round((n / total) * 100);
  const buildPct = pct(poll.build_votes || 0);
  const passPct = pct(poll.pass_votes || 0);

  return (
    <div className="poll-gold-card" data-testid={`community-poll-${poll.id}`}>
      <div className="poll-gold-inner">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: '5px', color: '#facc15', fontWeight: 800, fontSize: '0.64rem', letterSpacing: '0.05em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
            <Lightbulb size={12} /> Beatseeker Suggestion
          </span>
        </div>

        <p style={{ color: '#e6e9f2', fontSize: '0.8rem', lineHeight: 1.45, margin: '0 0 10px' }}>{poll.text}</p>

        {!voted ? (
          <div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => onVote(poll.id, 'build')}
                data-testid={`poll-vote-build-${poll.id}`}
                className="poll-vote-btn"
                style={{ border: '1px solid rgba(0,212,255,0.5)', color: '#00d4ff', background: 'rgba(0,212,255,0.08)' }}
              >
                🔥 Build it
              </button>
              <button
                onClick={() => onVote(poll.id, 'pass')}
                data-testid={`poll-vote-pass-${poll.id}`}
                className="poll-vote-btn"
                style={{ border: '1px solid rgba(236,72,153,0.5)', color: '#ec4899', background: 'rgba(236,72,153,0.08)' }}
              >
                🤔 Pass
              </button>
            </div>
            <div style={{ marginTop: '7px', textAlign: 'right', color: '#9ca3af', fontSize: '0.62rem' }}>⏳ {daysLeft(poll.expires_at)}</div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '7px' }} data-testid={`poll-results-${poll.id}`}>
            <div className="poll-bar-row" onClick={() => onVote(poll.id, 'build')} style={{ cursor: 'pointer' }}>
              <div className="poll-bar-track">
                <div className="poll-bar-fill" style={{ width: `${buildPct}%`, background: 'linear-gradient(90deg, rgba(0,212,255,0.55), rgba(0,212,255,0.25))' }} />
                <span className="poll-bar-label" style={{ fontWeight: poll.user_vote === 'build' ? 800 : 500 }}>
                  🔥 Build it {poll.user_vote === 'build' && '✓'}
                </span>
                <span className="poll-bar-pct">{buildPct}%</span>
              </div>
            </div>
            <div className="poll-bar-row" onClick={() => onVote(poll.id, 'pass')} style={{ cursor: 'pointer' }}>
              <div className="poll-bar-track">
                <div className="poll-bar-fill" style={{ width: `${passPct}%`, background: 'linear-gradient(90deg, rgba(236,72,153,0.55), rgba(236,72,153,0.25))' }} />
                <span className="poll-bar-label" style={{ fontWeight: poll.user_vote === 'pass' ? 800 : 500 }}>
                  🤔 Pass {poll.user_vote === 'pass' && '✓'}
                </span>
                <span className="poll-bar-pct">{passPct}%</span>
              </div>
            </div>
            <span style={{ color: '#6b7280', fontSize: '0.66rem' }}>{total} Beatseeker{total === 1 ? '' : 's'} weighed in · tap to change your vote · ⏳ {daysLeft(poll.expires_at)}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default PollCard;
